type Options = { title?: string };

export function createTabBadge(opts: Options = {}) {
  function ensureFaviconLink(): HTMLLinkElement {
    let el = document.querySelector<HTMLLinkElement>("link[rel*='icon']");
    if (!el) {
      el = document.createElement('link');
      el.rel = 'icon';
      el.type = 'image/png';
      document.head.appendChild(el);
    }
    return el;
  }

  const baseTitle = opts.title ?? document.title;
  const link = ensureFaviconLink();
  const originalHref = link.href;

  function clear() {
    document.title = baseTitle;
    if (originalHref && originalHref !== window.location.href) {
      link.href = originalHref;
    } else {
      link.removeAttribute('href');
    }
  }

  // Versão alternativa usando canvas para maior compatibilidade
  function createBadgeFaviconCanvas(count: number): string {
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
    const ctx = canvas.getContext('2d')!;

    const label = count > 99 ? '99+' : String(count);

    // Fundo do ícone (simula um favicon base)
    ctx.fillStyle = '#1f2937';
    ctx.beginPath();
    ctx.arc(16, 16, 14, 0, 2 * Math.PI);
    ctx.fill();

    ctx.strokeStyle = '#374151';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Badge vermelha
    ctx.fillStyle = '#dc2626';
    ctx.beginPath();
    ctx.arc(24, 8, 8, 0, 2 * Math.PI);
    ctx.fill();

    // Número da badge
    ctx.fillStyle = 'white';
    ctx.font = `bold ${label.length > 2 ? '8' : '10'}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, 24, 8);

    return canvas.toDataURL('image/png');
  }

  // Tenta sobrepor no favicon original se existir
  async function createWithOriginalFavicon(count: number): Promise<string> {
    if (!originalHref || originalHref === window.location.href) {
      return createBadgeFaviconCanvas(count);
    }

    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      canvas.width = 32;
      canvas.height = 32;
      const ctx = canvas.getContext('2d')!;

      const img = new Image();
      img.crossOrigin = 'anonymous';

      const timeout = setTimeout(() => {
        resolve(createBadgeFaviconCanvas(count));
      }, 1000); // Timeout de 1 segundo

      img.onload = () => {
        clearTimeout(timeout);

        // Desenha o favicon original
        ctx.drawImage(img, 0, 0, 32, 32);

        // Adiciona a badge
        const label = count > 99 ? '99+' : String(count);

        // Badge vermelha
        ctx.fillStyle = '#dc2626';
        ctx.beginPath();
        ctx.arc(24, 8, 8, 0, 2 * Math.PI);
        ctx.fill();

        // Borda branca na badge para contraste
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 1;
        ctx.stroke();

        // Número da badge
        ctx.fillStyle = 'white';
        ctx.font = `bold ${label.length > 2 ? '7' : '9'}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(label, 24, 8);

        resolve(canvas.toDataURL('image/png'));
      };

      img.onerror = () => {
        clearTimeout(timeout);
        resolve(createBadgeFaviconCanvas(count));
      };

      img.src = originalHref;
    });
  }

  async function set(count: number) {
    const n = Math.max(0, count);
    if (n > 0) {
      document.title = `(${n > 99 ? '99+' : n}) ${baseTitle}`;

      try {
        const faviconWithBadge = await createWithOriginalFavicon(n);
        link.href = faviconWithBadge;
      } catch (error) {
        // Fallback para versão canvas simples
        link.href = createBadgeFaviconCanvas(n);
      }
    } else {
      clear();
    }
  }

  return { set, clear };
}
